import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Tag, Space, Modal, Form, Input, Select,
  Typography, Row, Col, Card, message, Tooltip,
} from 'antd';
import { Plus, UserCog, Power } from 'lucide-react';
import { usersApi } from '../../services/api';

const { Title } = Typography;

const MODULE_OPTIONS = [
  { label: 'Leads', value: 'leads' },
  { label: 'Propiedades', value: 'properties' },
  { label: 'Oportunidades', value: 'opportunities' },
  { label: 'Reuniones', value: 'meetings' },
  { label: 'Blog', value: 'blog' },
  { label: 'Assets', value: 'assets' },
  { label: 'Integraciones', value: 'integrations' },
  { label: 'Valorador AVM', value: 'avm' },
  { label: 'Listado Particulares', value: 'listado-particulares' },
  { label: 'Inmovilla', value: 'inmovilla' },
  { label: 'Idealista', value: 'idealista' },
];

function parsePermissions(permissions: string): string[] {
  if (!permissions || permissions === 'all') return MODULE_OPTIONS.map(m => m.value);
  try { return JSON.parse(permissions); } catch { return []; }
}

export default function UsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await usersApi.list();
      setUsers(data.data || []);
    } catch {
      message.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (values: any) => {
    setSaving(true);
    try {
      await usersApi.create({ ...values, role: values.role || 'AGENT' });
      message.success('Usuario creado');
      setCreateOpen(false);
      form.resetFields();
      load();
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Error al crear usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id: string, name: string) => {
    Modal.confirm({
      title: `¿Desactivar a ${name}?`,
      content: 'El usuario no podrá iniciar sesión. Puedes reactivarlo después.',
      okText: 'Desactivar',
      okType: 'danger',
      onOk: async () => {
        try {
          await usersApi.deactivate(id);
          message.success('Usuario desactivado');
          load();
        } catch {
          message.error('Error al desactivar');
        }
      },
    });
  };

  const handleReactivate = async (id: string) => {
    try {
      await usersApi.update(id, { isActive: true });
      message.success('Usuario reactivado');
      load();
    } catch {
      message.error('Error al reactivar');
    }
  };

  const handlePermissions = async (id: string, permissions: string) => {
    const current = parsePermissions(permissions);
    let selected = [...current];

    Modal.confirm({
      title: 'Permisos de módulos',
      width: 500,
      content: (
        <div style={{ marginTop: 12 }}>
          <Select
            mode="multiple"
            defaultValue={current}
            options={MODULE_OPTIONS}
            style={{ width: '100%' }}
            onChange={(vals) => { selected = vals; }}
          />
        </div>
      ),
      onOk: async () => {
        try {
          await usersApi.updatePermissions(id, selected);
          message.success('Permisos actualizados');
          load();
        } catch {
          message.error('Error al actualizar permisos');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      render: (name: string, record: any) => (
        <a onClick={() => navigate(`/users/${record.id}`)}>{name}</a>
      ),
    },
    { title: 'Email', dataIndex: 'email' },
    {
      title: 'Rol',
      dataIndex: 'role',
      render: (role: string) => (
        <Tag color={role === 'ADMIN' ? 'gold' : 'blue'}>{role}</Tag>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'isActive',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'red'}>{active ? 'Activo' : 'Inactivo'}</Tag>
      ),
    },
    {
      title: 'Módulos',
      dataIndex: 'permissions',
      render: (permissions: string, record: any) => {
        if (record.role === 'ADMIN') return <Tag color="gold">Todos</Tag>;
        const mods = parsePermissions(permissions);
        return (
          <span style={{ fontSize: 12, color: '#666' }}>
            {mods.length} módulo{mods.length !== 1 ? 's' : ''}
          </span>
        );
      },
    },
    {
      title: 'Asignados',
      render: (_: any, record: any) => (
        <span style={{ fontSize: 12, color: '#666' }}>
          {record._count?.leads || 0} leads · {record._count?.properties || 0} props
        </span>
      ),
    },
    {
      title: 'Acciones',
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" onClick={() => navigate(`/users/${record.id}`)}>
            Ver
          </Button>
          {record.role !== 'ADMIN' && (
            <Tooltip title="Editar permisos de módulos">
              <Button size="small" icon={<UserCog size={14} />} onClick={() => handlePermissions(record.id, record.permissions)} />
            </Tooltip>
          )}
          <Tooltip title={record.isActive ? 'Desactivar' : 'Reactivar'}>
            <Button
              size="small"
              danger={record.isActive}
              icon={<Power size={14} />}
              onClick={() =>
                record.isActive
                  ? handleDeactivate(record.id, record.name)
                  : handleReactivate(record.id)
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Usuarios y Agentes</Title>
        </Col>
        <Col>
          <Button type="primary" icon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
            Nuevo usuario
          </Button>
        </Col>
      </Row>

      <Card>
        <Table
          dataSource={users}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        title="Crear usuario"
        open={createOpen}
        onCancel={() => { setCreateOpen(false); form.resetFields(); }}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText="Crear"
      >
        <Form form={form} layout="vertical" onFinish={handleCreate} style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Nombre completo" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="password" label="Contraseña" rules={[{ required: true, min: 6 }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="role" label="Rol" initialValue="AGENT">
            <Select options={[{ label: 'Agente', value: 'AGENT' }, { label: 'Admin', value: 'ADMIN' }]} />
          </Form.Item>
          <Form.Item name="phone" label="Teléfono">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
